import {
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
  Link,
} from "@mui/material";
// hooks
import useAuth from "../../hooks/useAuth";
import useResponsive from "../../hooks/useResponsive";
// components
import SvgIconStyle from "../../components/SvgIconStyle";
import Markdown from "../../components/Markdown";
// utils
import axios from "../../utils/axios";

// ------------------------------------------------------------

const PHASE_OPTIONS = {
  "new": "처음부터 서비스를 개발해야 합니다.",
  "maintain": "이미 개발된 서비스가 있습니다.",
};

const MAINTASK_OPTIONS = {
  "work": <Typography variant="body2" display="inline"><b>실무 위주</b>: 설계, 코딩 등 실무 개발 작업</Typography>,
  "consulting": <Typography variant="body2" display="inline"><b>컨설팅 위주</b>: 구조 확인, 코드 리뷰 등 컨설팅 업무</Typography>,
};

const PREFERRED_EXP_OPTIONS = {
  "technical": "스택에 대한 기술적 이해도가 높은 분",
  "startup": "스타트업에서 고군분투한 경험이 있는 분",
  "bigtech": "빅테크에서 체계적으로 주니어를 리드한 경험이 있는 분",
};

const ISSUE_OPTIONS = (smUp) => {
  return {
    "speed": "서비스 속도가 너무 느려요.",
    "scale": smUp ? "확장성이 없어서 새로운 기능을 추가하기 어려워요." : "새로운 기능을 추가하기 어려워요.",
    "schedule": smUp ? "예상 못한 이슈로 배포 일정을 지키기 어려워요." : "배포 일정을 지키기 어려워요.",
    "culture": smUp ? "지금 문제는 없지만, 체계적인 개발 문화를 이식하고 싶어요." : "체계적인 개발 문화를 이식하고 싶어요.",
    "bug": "버그가 많이 발생해요.",
    "collaboration": smUp ? "급하게 개발하느라 코드가 너무 지저분하고 협업하기 어려워요." : "급하게 개발하느라 코드가 지저분해요.",
  }
};

export default function ContentCard({ jobPost }) {
  const { user } = useAuth();
  const smUp = useResponsive("up", "sm");

  const openFile = async (item) => {
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const response = await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/files/${item.id}/url/`);
    window.open(response.data.url);
  };

  return (
    <Card
      sx={{
        mt: 2,
        p: 3,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="column"
        spacing={smUp ? 4 : 3}
        divider={<Divider orientation={"horizontal"} flexItem />}
        sx={{ width: 1 }}
      >
        {/* left side */}
        <Stack sx={{ width: smUp ? "64%" : 1 }}>
          <Stack spacing={2.5}>
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                포지션
              </Typography>
              {(jobPost?.detail_role || jobPost?.get_position_display) ? (
                <Typography variant="body2">
                  {jobPost?.detail_role?.card_display_name || jobPost?.detail_role?.role || jobPost?.get_position_display}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  미선택
                </Typography>
              )}
            </Stack>
            {jobPost?.intro_to_crew && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                회사 / 서비스 소개
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line", wordBreak: "break-all" }}>
                <Markdown>
                  {jobPost?.intro_to_crew}
                </Markdown>
              </Typography>
            </Stack>
            )}
            {jobPost?.monthly_budget && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                월 예산
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
                {jobPost?.monthly_budget}
              </Typography>
            </Stack>
            )}
            {jobPost?.task_for_4weeks && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                요청할 업무 상세 내용
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
                {jobPost?.task_for_4weeks}
              </Typography>
            </Stack>
            )}
            {jobPost?.files?.length > 0 && (
              <Stack spacing={.5}>
                <Typography variant="subtitle2">
                  업무 관련 자료
                </Typography>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {jobPost?.files?.map((item, index) => (
                    <Button key={index} sx={{textTransform: 'none', fontWeight: "normal", textDecoration: "underline"}} startIcon={<SvgIconStyle src={`/icons/ic_attach_file2.svg`} sx={{ minWidth: 16, minHeight: 16, width: 16, height: 16 }} />} onClick={() => openFile(item)}>
                      {item.name}
                    </Button>
                  ))}
                </Stack>
              </Stack>
            )}
            {jobPost?.agree_to_open && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  스피드 매칭
                </Typography>
                <Typography variant="body2">
                  네. 스피드매칭 진행을 희망합니다.
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        {/* right side */}
        <Stack sx={{ width: smUp ? "36%" : 1 }}>
          <Stack spacing={2.5}>
            {/* 개발자 한정 */}
            {jobPost?.position === 'developer' && jobPost?.context_phase && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                현재 서비스 개발 단계
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Chip variant="outlined" label={PHASE_OPTIONS[jobPost.context_phase]} />
              </Stack>
            </Stack>
            )}
            {jobPost?.position === 'developer' && jobPost?.context_main_task && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                주로 요청할 업무
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Chip variant="outlined" label={MAINTASK_OPTIONS[jobPost.context_main_task]} />
              </Stack>
            </Stack>
            )}
            {jobPost?.position === 'developer' && (jobPost?.context_main_task || jobPost?.context_phase) && jobPost?.context_collaborator && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                협업하는 내부 인력
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Chip variant="outlined" label={jobPost.context_collaborator} />
              </Stack>
            </Stack>
            )}
            {/* 테크리드 한정 */}
            {jobPost?.position === 'developer' && jobPost?.context_technical_issues && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                현재 겪고 있는 기술적인 문제
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {jobPost.context_technical_issues.split(',').map((c, index) => <Chip variant="outlined" key={index} label={ISSUE_OPTIONS(smUp)[c]} />)}
              </Stack>
            </Stack>
            )}

            {jobPost?.position === 'developer' && jobPost?.context_preferred_experience && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                선호하는 테크리드 경험
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Chip variant="outlined" label={PREFERRED_EXP_OPTIONS[jobPost.context_preferred_experience]} />
              </Stack>
            </Stack>
            )}

            {jobPost?.position === 'developer' && jobPost?.context_development_stack && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                찾고 있는 기술 스택
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {jobPost?.context_development_stack?.split(',').map((c, index) => <Chip variant="outlined" key={index} label={c}/>)}
              </Stack>
            </Stack>
            )}

            {/* 공통 */}
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                계약 방식
              </Typography>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Chip variant="outlined" label="원격 근무" />
                <Chip variant="outlined" label="파트타임" />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}